<template>
  <v-card>
    <v-list dense>
      <v-list-item>
        <v-list-item-content
          class="text-subtitle-1 align-end text-end my-n4"
          :class="item.default_flg ? 'font-weight-bold' : ''"
        >
          <v-row dense no-gutters align="center">
            {{ item.unit }}
            <v-checkbox
              v-if="
                item.default_flg ||
                isNew ||
                (editDefault && !item.status && !previewMode)
              "
              v-model="item.default_flg"
              :readonly="editDefault ? false : !isNew"
              class="mt-0 ms-3"
              hide-details
              @change="$emit('make-defalut', item)"
            >
              <template v-slot:label>
                <span class="caption">
                  {{ $t("Material.units.default_flg") }}
                </span>
              </template>
            </v-checkbox>
          </v-row>
        </v-list-item-content>
        <v-list-item-content class="align-end text-end my-n4">
          <v-row dense no-gutters align="center">
            <v-spacer />
            <v-switch
              v-if="
                editUnit && !item.default_flg && !item.new && item.rate >= 1
              "
              v-model="item.status"
              :true-value="0"
              :false-value="1"
              :label="
                item.status
                  ? $t('Material.unitFlgs.off')
                  : $t('Material.unitFlgs.on')
              "
            />
            <span v-else-if="!editUnit">
              <v-chip
                small
                label
                dark
                :color="item.status ? 'accent' : 'primary'"
              >
                {{
                  item.status
                    ? $t("Material.unitFlgs.off")
                    : $t("Material.unitFlgs.on")
                }}
              </v-chip>
            </span>
            <v-btn
              v-if="isNew || (savable && item.new && !item.default_flg)"
              small
              text
              color="danger"
              @click.stop="$emit('delete', item)"
            >
              {{ $t("CRUD.delete") }}
            </v-btn>
          </v-row>
        </v-list-item-content>
      </v-list-item>

      <v-list-item>
        <v-list-item-content>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ $t("Material.units.rate") }}{{ $t("Global.colon") }}
              </span>
            </template>
            <span>
              {{ $t("Material.units.rate_hint") }}
            </span>
          </v-tooltip>
        </v-list-item-content>
        <v-list-item-content
          v-if="
            (item.new && !item.default_flg) ||
            (editDefault &&
              item.origin_default_flg &&
              !item.default_flg &&
              !previewMode &&
              item.edit)
          "
          class="px-0 py-1"
        >
          <!-- 换算比例 -->
          <v-text-field
            single-line
            dense
            hide-details
            v-model="item.rate"
            type="number"
            min="1"
            @blur="
              item.rate =
                item.rate < 1
                  ? 1
                  : $numberFormatter.formatWithDotNoMoreThan(item.rate, 2)
            "
            @change="$emit('changeDefaultRate')"
          >
            <template v-slot:append-outer>
              <span style="min-width: 80px">
                {{ defaultUnit }} =1 {{ item.unit }}
              </span>
            </template>
          </v-text-field>
          <!-- :suffix="defaultUnit + ' / ' + item.unit" -->
        </v-list-item-content>
        <v-list-item-content v-else class="align-end text-end d-inline pa-1">
          {{ item.rate }}
          <span v-if="!item.default_flg">
            {{ defaultUnit }} =1 {{ item.unit }}
          </span>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <v-divider></v-divider>
    <ValidationObserver ref="observer">
      <v-list dense>
        <v-list-item>
          <v-list-item-content>
            {{ $t("Material.units.barcode") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content class="align-end text-end px-0 py-1">
            <v-text-field
              v-if="editUnit"
              single-line
              dense
              v-model="item.barcode"
              type="text"
              :error-messages="barcodeErrors(item.barcode)"
            />
            <v-tooltip right v-else>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="d-block text-truncate">
                  {{ item.barcode }}
                </span>
              </template>
              {{ item.barcode }}
            </v-tooltip>
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content>
            {{ $t("Material.units.net") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content
            v-if="editUnit"
            class="align-end text-end px-0 py-1"
          >
            <v-text-field
              single-line
              dense
              v-model="item.net"
              type="number"
              min="0"
              @blur="
                item.net = $numberFormatter.formatWithDotNoMoreThan(item.net, 3)
              "
            >
              <template v-slot:append-outer>
                <v-select
                  single-line
                  dense
                  class="ma-n1 pa-0"
                  style="max-width: 80px; margin: auto"
                  :items="net_units"
                  v-model="item.net_unit"
                >
                </v-select>
              </template>
            </v-text-field>
          </v-list-item-content>
          <v-list-item-content v-else class="align-end text-end px-0 py-1">
            <span>
              {{ item.net | formatWithDotNoMoreThan(3) }}
              {{ item.net_unit }}
            </span>
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content>
            {{ $t("Material.units.rough_weight") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content
            v-if="editUnit"
            class="align-end text-end px-0 py-1"
          >
            <v-text-field
              single-line
              dense
              v-model="item.gross_weight"
              type="number"
              min="0"
              @blur="
                item.gross_weight = $numberFormatter.formatWithDotNoMoreThan(
                  item.gross_weight,
                  3
                )
              "
            >
              <template v-slot:append-outer>
                <v-select
                  single-line
                  dense
                  class="ma-n1 pa-0"
                  style="max-width: 80px; margin: auto"
                  :items="rough_weight_units"
                  item-text="name"
                  item-value="name"
                  v-model="item.gross_weight_unit"
                >
                </v-select>
              </template>
            </v-text-field>
          </v-list-item-content>
          <v-list-item-content v-else class="align-end text-end px-0 py-1">
            <span>
              {{ item.gross_weight | formatWithDotNoMoreThan(3) }}
              {{ item.gross_weight_unit }}
            </span>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="settings && settings.count_unit && item.count_unit_flg"
        >
          <v-list-item-content>
            {{ $t("Material.units.count_unit") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content
            v-if="editUnit"
            class="align-end text-end px-0 py-1"
          >
            <v-text-field single-line dense v-model="item.count_unit">
            </v-text-field>
          </v-list-item-content>
          <v-list-item-content v-else class="align-end text-end px-0 py-1">
            <span>
              {{ item.count_unit }}
            </span>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="settings && settings.count_unit && item.count_unit_flg"
        >
          <v-list-item-content>
            {{ $t("Material.units.count_unit_rate") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content
            v-if="editUnit"
            class="align-end text-end px-0 py-1"
          >
            <v-text-field
              single-line
              dense
              v-model="item.count_unit_rate"
              type="number"
              min="0"
              @blur="
                item.count_unit_rate = $numberFormatter.formatWithDotNoMoreThan(
                  item.count_unit_rate,
                  3
                )
              "
            >
            </v-text-field>
          </v-list-item-content>
          <v-list-item-content v-else class="align-end text-end px-0 py-1">
            <span>
              {{ item.count_unit_rate | formatWithDotNoMoreThan(3) }}
            </span>
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content>
            {{ $t("Material.units.express_conversion_rate")
            }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content
            class="align-end text-end px-0 py-1"
            v-if="editUnit"
          >
            <v-text-field
              single-line
              dense
              v-model="item.express_conversion_rate"
              type="text"
            >
              <template v-slot:append-outer>
                <div style="width: 50px; margin: auto">
                  {{ $t("Material.units.express_rate", [item.unit]) }}
                </div>
              </template>
            </v-text-field>
          </v-list-item-content>
          <v-list-item-content v-else class="align-end text-end px-0 py-1">
            <span>
              {{ item.express_conversion_rate }}
              {{ $t("Material.units.express_rate", [item.unit]) }}
            </span>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-content>
            {{ $t("Material.units.loss_percent") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content
            class="align-end text-end px-0 py-1"
            v-if="editUnit"
          >
            <ValidationProvider
              :name="$t('Material.units.loss_percent')"
              v-slot="{ errors }"
              rules="lessThan:100"
            >
              <v-text-field
                single-line
                dense
                v-model="item.loss_percent"
                type="number"
                @blur="
                  item.loss_percent = $numberFormatter.formatTwoDot(
                    item.loss_percent,
                    true
                  )
                "
                :error-messages="errors"
              >
                <template v-slot:append-outer>
                  <div style="width: 50px; margin: auto">
                    {{ $t("Global.rate") }}
                  </div>
                </template>
              </v-text-field>
            </ValidationProvider>
          </v-list-item-content>
          <v-list-item-content v-else class="align-end text-end px-0 py-1">
            <span>
              {{ item.loss_percent }}
              {{ $t("Global.rate") }}
            </span>
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content>
            {{ $t("Material.units.comment") }}{{ $t("Global.colon") }}
          </v-list-item-content>
          <v-list-item-content class="align-end text-end px-0 py-1">
            <v-text-field
              v-if="editUnit"
              single-line
              dense
              v-model="item.comment"
              type="text"
            />
            <v-tooltip right v-else>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="d-block text-truncate">
                  {{ item.comment }}
                </span>
              </template>
              {{ item.comment }}
            </v-tooltip>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </ValidationObserver>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import MaterialUnits from "@/api/material_units";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "StandardDetail",

  props: {
    value: Object,
    defaultUnit: String,
    mode: String,
    editUnit: Boolean,
    editDefault: Boolean,
    serverErrors: Array,
    previewMode: Boolean,
  },

  components: {
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    isView() {
      return !this.mode || this.mode == "view";
    },
    savable() {
      return this.mode == "edit" || this.mode == "new" || this.mode == "clone";
    },
    isNew() {
      return this.mode == "new" || this.mode == "clone";
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["net_units", "settings"]),
  },

  data: () => ({
    item: {},
    rough_weight_units: [],
  }),

  mounted() {
    MaterialUnits.fetchAllForSelect({ weight_flg: 1 }).then(
      function (response) {
        if (response.data.status.isOK()) {
          this.rough_weight_units = response.data.units;
        }
      }.bind(this)
    );
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        this.item = this.value;
      },
    },
    item: {
      deep: true,
      handler() {
        this.$emit("input", this.item);
      },
    },
  },

  methods: {
    barcodeErrors(barcode) {
      var errs = this.$resparse.getErrors(this.serverErrors, "barcode");
      return errs.filter((err) => err.text == barcode).map((err) => err.msg);
    },
  },
};
</script>
